import { useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../../i18n/localization";
import { useAdminSession } from "./AdminSessionGate";
import {
  checkBlock, createWahaActions, initialWahaUi, testBlock, type WahaActionBlock, type WahaActions, type WahaUiState
} from "./wahaSettingsActions";
import { checkWahaConnection, getWahaSettings, saveWahaSettings, sendWahaTestMessage } from "./wahaSettingsModel";

// Admin → Settings → WhatsApp (WAHA) delivery. Settings are stored encrypted on the server.
// Saving never sends anything; "Check connection" is read-only; only "Send test message" sends,
// and only to the SAVED test recipient shown next to the button. The API key is write-only:
// never shown, never stored in the browser, and cleared from the form after every save attempt.
export function WahaSettings() {
  const { t } = useI18n();
  const { session, guard } = useAdminSession();
  const [ui, setUi] = useState<WahaUiState>(initialWahaUi);
  const latest = useRef(ui);
  latest.current = ui;

  const actions = useMemo(() => createWahaActions({
    get: () => guard(getWahaSettings(session.token)),
    save: (payload) => guard(saveWahaSettings(session.token, payload)),
    check: () => guard(checkWahaConnection(session.token)),
    sendTest: () => guard(sendWahaTestMessage(session.token))
  }, () => latest.current, (patch) => setUi((s) => ({ ...s, ...patch(s) }))), [session.token, guard]);

  useEffect(() => { void actions.load(); }, [actions]);

  return <WahaSettingsPanel w={t.waha} ui={ui} actions={actions} />;
}

type WahaStrings = ReturnType<typeof useI18n>["t"]["waha"];

/** Presentational and hook-free (rendered directly in tests). Every control calls exactly one action. */
export function WahaSettingsPanel({ w, ui, actions }: { w: WahaStrings; ui: WahaUiState; actions: WahaActions }) {
  const { view, form, busy } = ui;
  const blockText = (b: WahaActionBlock) =>
    b === "incomplete" ? w.actionsNeedComplete : b === "unsaved" ? w.actionsNeedSave : b === "noRecipient" ? w.testNeedsRecipient : null;
  const checkBlocked = blockText(checkBlock(ui));
  const testBlocked = blockText(testBlock(ui));

  return (
    <section aria-labelledby="admin-waha-settings" style={card}>
      <h2 id="admin-waha-settings" style={{ fontSize: 15, color: "#14305f", margin: "0 0 6px" }}>{w.title}</h2>

      {ui.loadError && <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{w.errors[ui.loadError]}</div>}
      {!form && !ui.loadError && <p role="status" style={{ color: "#5b6b82", fontSize: 13 }}>{w.loading}</p>}

      {form && view && (
        <>
          <div role="status" style={view.deliveryActive ? activeBox : statusBox}>
            <strong>{view.deliveryActive ? w.activeTitle : w.offTitle}</strong>
            <div style={{ marginTop: 4 }}>{view.deliveryActive ? w.activeText : w.offText}</div>
          </div>

          <form data-action="save" onSubmit={(e) => { e.preventDefault(); void actions.save(); }} noValidate>
            <p style={{ color: "#64748b", fontSize: 12, margin: "0 0 12px" }}>
              {view.configured ? (view.complete ? w.stateComplete : w.stateDraft) : w.stateEmpty}
            </p>

            <label style={field}>
              {w.baseUrl}
              <input type="url" inputMode="url" dir="ltr" autoComplete="off" spellCheck={false} maxLength={512}
                value={form.baseUrl} onChange={(e) => actions.update({ baseUrl: e.target.value })}
                placeholder="https://waha.example.internal" aria-describedby="waha-url-hint" style={input} />
            </label>
            <div id="waha-url-hint" style={hint}>{w.baseUrlHint}</div>

            <label style={field}>
              {w.session}
              <input dir="ltr" autoComplete="off" spellCheck={false} maxLength={64}
                value={form.session} onChange={(e) => actions.update({ session: e.target.value })} style={input} />
            </label>

            <label style={field}>
              {w.apiKey}
              <input type="password" dir="ltr" autoComplete="new-password" spellCheck={false} maxLength={512}
                value={form.apiKey} onChange={(e) => actions.update({ apiKey: e.target.value, clearApiKey: false })}
                placeholder={view.hasApiKey ? w.apiKeyConfigured : w.apiKeyEmpty}
                aria-describedby="waha-key-hint" style={input} />
            </label>
            <div id="waha-key-hint" style={hint}>{view.hasApiKey ? w.apiKeyKeepHint : w.apiKeyWriteOnlyHint}</div>

            {view.hasApiKey && (
              <label style={checkRow}>
                <input type="checkbox" checked={form.clearApiKey} disabled={form.enabled}
                  onChange={(e) => actions.update({ clearApiKey: e.target.checked, apiKey: "" })} />
                {w.clearApiKey}
              </label>
            )}
            {view.hasApiKey && form.enabled && <div style={hint}>{w.clearNeedsDisable}</div>}

            <label style={field}>
              {w.testRecipient}
              <input type="tel" inputMode="tel" dir="ltr" autoComplete="off" maxLength={32}
                value={form.testRecipient} onChange={(e) => actions.update({ testRecipient: e.target.value })}
                placeholder="+1-868-1234567" aria-describedby="waha-recipient-hint" style={input} />
            </label>
            <div id="waha-recipient-hint" style={hint}>{w.testRecipientHint}</div>

            <label style={{ ...checkRow, fontWeight: 600 }}>
              <input type="checkbox" checked={form.enabled}
                onChange={(e) => actions.update({ enabled: e.target.checked, clearApiKey: e.target.checked ? false : form.clearApiKey })} />
              {w.enable}
            </label>
            <div style={hint}>{w.enableHint}</div>

            <div aria-live="polite" style={{ minHeight: 18, margin: "8px 0", fontSize: 13 }}>
              {ui.error && <div role="alert" style={{ color: "#b91c1c" }}>{w.errors[ui.error]}</div>}
              {ui.saved && !ui.error && <div role="status" style={{ color: "#15803d" }}>{view.deliveryActive ? w.savedActive : w.savedOff}</div>}
            </div>

            <button type="submit" disabled={busy !== null} aria-busy={busy === "save"} style={{ ...primaryBtn, background: busy ? "#94a3b8" : "#1d4ed8" }}>
              {busy === "save" ? w.saving : w.save}
            </button>
          </form>

          <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "18px 0" }} />

          <h3 style={subTitle}>{w.checkTitle}</h3>
          <p style={hint}>{w.checkHint}</p>
          {checkBlocked && <p style={{ ...hint, color: "#b45309" }}>{checkBlocked}</p>}
          <button type="button" data-action="check" onClick={() => void actions.check()} disabled={busy !== null || !!checkBlocked}
            aria-busy={busy === "check"} style={secondaryBtn(busy !== null || !!checkBlocked)}>
            {busy === "check" ? w.checking : w.check}
          </button>
          <div aria-live="polite" style={{ minHeight: 18, margin: "8px 0", fontSize: 13 }}>
            {ui.checkResult && <div role="status" style={{ color: ui.checkResult === "working" ? "#15803d" : "#b91c1c" }}>{w.checkResults[ui.checkResult]}</div>}
            {ui.checkError && <div role="alert" style={{ color: "#b91c1c" }}>{w.errors[ui.checkError]}</div>}
          </div>

          <h3 style={subTitle}>{w.testTitle}</h3>
          <div role="note" style={warnBox}>{w.testWarning}</div>
          <p id="waha-test-target" style={{ ...field, margin: "0 0 8px" }}>
            {w.testTarget}{" "}
            <strong data-testid="waha-test-target" dir="ltr" style={{ unicodeBidi: "isolate" }}>{view.testRecipientDisplay ?? w.testTargetNone}</strong>
          </p>
          {testBlocked && <p style={{ ...hint, color: "#b45309" }}>{testBlocked}</p>}
          <button type="button" data-action="send-test" onClick={() => void actions.sendTest()} disabled={busy !== null || !!testBlocked}
            aria-busy={busy === "test"} aria-describedby="waha-test-target" style={secondaryBtn(busy !== null || !!testBlocked)}>
            {busy === "test" ? w.sendingTest : w.sendTest}
          </button>
          <div aria-live="polite" style={{ minHeight: 18, margin: "8px 0", fontSize: 13 }}>
            {ui.testResult && <div role="status" data-testid="waha-test-result" style={{ color: ui.testResult === "accepted" ? "#15803d" : "#b91c1c" }}>{w.testResults[ui.testResult]}</div>}
            {ui.testError && <div role="alert" style={{ color: "#b91c1c" }}>{w.errors[ui.testError]}</div>}
          </div>
        </>
      )}
    </section>
  );
}

const card: React.CSSProperties = { border: "1px solid #e2e8f0", borderRadius: 10, padding: 18, background: "#fff", marginBottom: 16, maxWidth: 640 };
const statusBox: React.CSSProperties = {
  background: "#eef3fb", border: "1px solid #d6deea", color: "#14305f", borderRadius: 8, padding: "10px 12px", fontSize: 13, margin: "0 0 14px", lineHeight: 1.5
};
const activeBox: React.CSSProperties = { ...statusBox, background: "#dcfce7", border: "1px solid #bbf7d0", color: "#14532d" };
const warnBox: React.CSSProperties = {
  background: "#fef3c7", border: "1px solid #fcd34d", color: "#78350f", borderRadius: 8, padding: "8px 12px", fontSize: 12, margin: "0 0 10px", lineHeight: 1.5
};
const field: React.CSSProperties = { display: "block", color: "#14305f", fontSize: 13, marginBottom: 4 };
const checkRow: React.CSSProperties = { display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#14305f", margin: "0 0 6px" };
const hint: React.CSSProperties = { color: "#64748b", fontSize: 12, margin: "0 0 12px", lineHeight: 1.45 };
const subTitle: React.CSSProperties = { fontSize: 14, color: "#14305f", margin: "0 0 4px" };
const input: React.CSSProperties = {
  display: "block", width: "100%", marginTop: 4, padding: "8px 10px", border: "1px solid #d6deea", borderRadius: 6,
  boxSizing: "border-box", fontSize: 14, color: "#1e2a3a"
};
const primaryBtn: React.CSSProperties = { color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", cursor: "pointer", fontSize: 13 };
function secondaryBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? "#e2e8f0" : "#14305f", color: disabled ? "#94a3b8" : "#fff", border: "none", borderRadius: 6,
    padding: "7px 16px", cursor: disabled ? "not-allowed" : "pointer", fontSize: 13, marginTop: 4
  };
}
